/**
 * Scheduling windows and conflict detection.
 *
 * The dispatch board drags optimistically; the server is the authority. Both
 * run the same `findConflicts` so the board can grey out a drop target before
 * the round trip and the server can still refuse it if two dispatchers raced.
 *
 * Windows are half-open: [start, end). A job ending at 10:00 and one starting
 * at 10:00 do not conflict.
 */
import { OCCUPYING_JOB_STATUSES } from './job-status.js';
export function toDate(value) {
    return typeof value === 'string' ? new Date(value) : value;
}
export function isValidWindow(start, end) {
    return (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end.getTime() > start.getTime());
}
export function windowsOverlap(a, b) {
    return a.start.getTime() < b.end.getTime() && b.start.getTime() < a.end.getTime();
}
export function overlapMinutes(a, b) {
    const ms = Math.min(a.end.getTime(), b.end.getTime()) - Math.max(a.start.getTime(), b.start.getTime());
    return ms > 0 ? Math.round(ms / 60_000) : 0;
}
/**
 * Every existing job that would double-book `techId` over [start, end).
 * Cancelled and unscheduled jobs hold no slot and are ignored.
 */
export function findConflicts(query, jobs) {
    const target = { start: query.start, end: query.end };
    if (!isValidWindow(target.start, target.end))
        return [];
    const conflicts = [];
    for (const job of jobs) {
        if (job.id === query.excludeJobId)
            continue;
        if (job.assignedTechId !== query.techId)
            continue;
        if (!OCCUPYING_JOB_STATUSES.includes(job.status))
            continue;
        if (!job.scheduledStart || !job.scheduledEnd)
            continue;
        const other = { start: toDate(job.scheduledStart), end: toDate(job.scheduledEnd) };
        if (!isValidWindow(other.start, other.end))
            continue;
        if (!windowsOverlap(target, other))
            continue;
        conflicts.push({
            id: job.id,
            techId: query.techId,
            start: other.start,
            end: other.end,
            overlapMinutes: overlapMinutes(target, other),
        });
    }
    return conflicts.sort((a, b) => a.start.getTime() - b.start.getTime());
}
export function hasConflict(query, jobs) {
    return findConflicts(query, jobs).length > 0;
}
export const DEFAULT_CALENDAR = {
    dayStartHour: 7,
    dayEndHour: 19,
    snapMinutes: 15,
};
export function startOfDay(d) {
    const out = new Date(d);
    out.setHours(0, 0, 0, 0);
    return out;
}
export function endOfDay(d) {
    const out = startOfDay(d);
    out.setDate(out.getDate() + 1);
    return out;
}
export function addDays(d, n) {
    const out = new Date(d);
    out.setDate(out.getDate() + n);
    return out;
}
export function addMinutes(d, n) {
    return new Date(d.getTime() + n * 60_000);
}
/** Monday-start week containing `d`. */
export function startOfWeek(d) {
    const out = startOfDay(d);
    const dow = (out.getDay() + 6) % 7; // Mon = 0
    out.setDate(out.getDate() - dow);
    return out;
}
export function weekDays(d) {
    const start = startOfWeek(d);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}
export function snapToIncrement(d, minutes) {
    const ms = minutes * 60_000;
    return new Date(Math.round(d.getTime() / ms) * ms);
}
export function durationMinutes(start, end) {
    return Math.round((toDate(end).getTime() - toDate(start).getTime()) / 60_000);
}
/** Fractional position of a time within the visible board, 0..1. */
export function positionInDay(d, cfg = DEFAULT_CALENDAR) {
    const minutes = d.getHours() * 60 + d.getMinutes();
    const startM = cfg.dayStartHour * 60;
    const endM = cfg.dayEndHour * 60;
    return (minutes - startM) / (endM - startM);
}
/** Inverse of `positionInDay`: which time does this fraction of the board mean? */
export function timeFromPosition(day, fraction, cfg = DEFAULT_CALENDAR) {
    const startM = cfg.dayStartHour * 60;
    const endM = cfg.dayEndHour * 60;
    const minutes = startM + fraction * (endM - startM);
    const out = startOfDay(day);
    out.setMinutes(Math.round(minutes));
    return snapToIncrement(out, cfg.snapMinutes);
}
/**
 * Lay overlapping blocks out side by side. Returns a column index and column
 * count per job so a day column can render two 9am jobs at half width each.
 */
export function layoutOverlaps(items) {
    const sorted = [...items].sort((a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime());
    const out = [];
    let cluster = [];
    let clusterEnd = -Infinity;
    const flush = () => {
        const width = cluster.reduce((m, c) => Math.max(m, c.column + 1), 0);
        for (const c of cluster)
            c.columns = width;
        out.push(...cluster);
        cluster = [];
        clusterEnd = -Infinity;
    };
    for (const item of sorted) {
        if (item.start.getTime() >= clusterEnd && cluster.length > 0)
            flush();
        const taken = new Set(cluster.filter((c) => c.end.getTime() > item.start.getTime()).map((c) => c.column));
        let column = 0;
        while (taken.has(column))
            column++;
        cluster.push({ ...item, column, columns: 1 });
        clusterEnd = Math.max(clusterEnd, item.end.getTime());
    }
    if (cluster.length > 0)
        flush();
    return out;
}
export function formatWindow(start, end, locale = 'en-US') {
    const s = toDate(start);
    const e = toDate(end);
    const fmt = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' });
    return `${fmt.format(s)} – ${fmt.format(e)}`;
}
export function formatDayLabel(d, locale = 'en-US') {
    return new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }).format(toDate(d));
}
//# sourceMappingURL=scheduling.js.map